import React, { useState, useEffect } from "react";
import {
  Button,
  Box,
  useColorModeValue,
  Heading,
  Text,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Flex,
  Wrap,
  WrapItem,
  Icon,
  Divider,
} from "@chakra-ui/react";
import { db } from "../firebase.config";
import { collection, getDocs } from "firebase/firestore";
import { Link } from "react-router-dom";
import { ScaleLoader } from "react-spinners";
import { ImLocation } from "react-icons/im";
import { FaStore, FaUserCircle, FaWhatsapp, FaPhoneAlt, FaSearch, FaTimes, FaBuilding } from "react-icons/fa";
import { MdLocationCity } from "react-icons/md";
import moment from "moment";
import { Helmet } from "react-helmet";

const SeeAllFeaturedSellers = () => {
  const [sellers, setSellers] = useState([]);
  const [filteredSellers, setFilteredSellers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchName, setSearchName] = useState("");
  const [searchCity, setSearchCity] = useState("");
  const [searchLocation, setSearchLocation] = useState("");
  const [uniqueCities, setUniqueCities] = useState([]);

  const cardBg = useColorModeValue("white", "gray.800");
  const shadowColor = useColorModeValue(
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
  );

  useEffect(() => {
    const fetchSellers = async () => {
      try {
        setLoading(true);
        const sellersRef = collection(db, "Featured-Agents");
        const qSnap = await getDocs(sellersRef);
        const sellersArray = [];
        qSnap.forEach((doc) => {
          sellersArray.push({ id: doc.id, ...doc.data() });
        });
        setSellers(sellersArray);
        setFilteredSellers(sellersArray);
        
        // Extract unique cities from seller addresses
        const cities = new Set();
        sellersArray.forEach(seller => {
          if (seller.city) {
            cities.add(seller.city);
          } else if (seller.address) {
            // Try to extract city from address (common Pakistani cities)
            const address = seller.address.toLowerCase();
            const pakistaniCities = [
              'karachi', 'lahore', 'islamabad', 'rawalpindi', 'multan', 
              'faisalabad', 'peshawar', 'quetta', 'gujranwala', 'sialkot',
              'abbottabad', 'swat', 'murree', 'bahawalpur', 'sukkur',
              'hyderabad', 'dubai', 'ajman', 'ras al khaimah'
            ];
            pakistaniCities.forEach(city => {
              if (address.includes(city)) {
                cities.add(city.charAt(0).toUpperCase() + city.slice(1));
              }
            });
          }
        });
        setUniqueCities(Array.from(cities).sort());
        
        setLoading(false);
      } catch (error) {
        setLoading(false);
        setError("Failed to load featured sellers");
      }
    };

    fetchSellers();
  }, []);

  // Filter sellers based on search criteria
  useEffect(() => {
    let filtered = [...sellers];
    
    // Filter by name
    if (searchName.trim()) {
      filtered = filtered.filter(seller => 
        seller.sellerName?.toLowerCase().includes(searchName.toLowerCase()) ||
        seller.agentName?.toLowerCase().includes(searchName.toLowerCase()) ||
        seller.companyName?.toLowerCase().includes(searchName.toLowerCase())
      );
    }
    
    // Filter by city
    if (searchCity && searchCity !== "all") {
      filtered = filtered.filter(seller => {
        if (seller.city) {
          return seller.city.toLowerCase() === searchCity.toLowerCase();
        }
        if (seller.address) {
          return seller.address.toLowerCase().includes(searchCity.toLowerCase());
        }
        return false;
      });
    }
    
    // Filter by location (more detailed search)
    if (searchLocation.trim()) {
      filtered = filtered.filter(seller => 
        seller.address?.toLowerCase().includes(searchLocation.toLowerCase()) ||
        seller.location?.toLowerCase().includes(searchLocation.toLowerCase()) ||
        seller.area?.toLowerCase().includes(searchLocation.toLowerCase())
      );
    }
    
    setFilteredSellers(filtered);
  }, [searchName, searchCity, searchLocation, sellers]);

  const clearFilters = () => {
    setSearchName("");
    setSearchCity("");
    setSearchLocation("");
  };

  const hasActiveFilters = searchName || (searchCity && searchCity !== "all") || searchLocation;

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen">
        <ScaleLoader color="#10b981" />
      </div>
    );

  if (error)
    return (
      <Box className="text-center p-8">
        <Heading size="lg" className="text-red-500 mb-4">
          {error}
        </Heading>
        <Link to="/" className="text-emerald-600 hover:underline">
          Return to homepage
        </Link>
      </Box>
    );

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <Helmet>
        <title>Premium Real Estate Agents | EstateGhar</title>
        <meta
          name="description"
          content="Explore our network of trusted and verified real estate agents across Pakistan. Find premium property dealers and agents for your real estate needs."
        />
      </Helmet>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <Heading as="h1" size="xl" className="text-gray-800 mb-2">
            Premium Real Estate Agents
          </Heading>
          <div className="w-24 h-1 bg-emerald-500 mx-auto mb-4"></div>
          <Text fontSize="lg" className="text-gray-600 max-w-2xl mx-auto">
            Explore our network of trusted and verified real estate agents
          </Text>
        </div>

        {/* Search Filter Bar */}
        <div className="bg-white rounded-xl shadow-md p-5 mb-8 border border-gray-100">
          <div className="flex items-center gap-2 mb-4">
            <FaSearch className="text-emerald-500" />
            <Text className="font-semibold text-gray-700">Search & Filter Agents</Text>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search by Name */}
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <FaUserCircle className="text-gray-400" />
              </InputLeftElement>
              <Input
                type="text"
                placeholder="Search by agent name..."
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
              />
            </InputGroup>

            {/* Filter by City */}
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <MdLocationCity className="text-gray-400" />
              </InputLeftElement>
              <select
                value={searchCity}
                onChange={(e) => setSearchCity(e.target.value)}
                className="w-full px-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white text-gray-700"
              >
                <option value="all">All Cities</option>
                {uniqueCities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
                <option value="karachi">Karachi</option>
                <option value="lahore">Lahore</option>
                <option value="islamabad">Islamabad</option>
                <option value="rawalpindi">Rawalpindi</option>
                <option value="multan">Multan</option>
                <option value="faisalabad">Faisalabad</option>
                <option value="peshawar">Peshawar</option>
                <option value="quetta">Quetta</option>
                <option value="gujranwala">Gujranwala</option>
                <option value="sialkot">Sialkot</option>
                <option value="abbottabad">Abbottabad</option>
                <option value="swat">Swat</option>
                <option value="murree">Murree</option>
              </select>
            </InputGroup>

            {/* Search by Location/Area */}
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <ImLocation className="text-gray-400" />
              </InputLeftElement>
              <Input
                type="text"
                placeholder="Search by area / locality..."
                value={searchLocation}
                onChange={(e) => setSearchLocation(e.target.value)}
                className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
              />
            </InputGroup>
          </div>

          {/* Active Filters & Clear Button */}
          {hasActiveFilters && (
            <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-100">
              <div className="flex flex-wrap gap-2">
                {searchName && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                    Name: {searchName}
                    <button onClick={() => setSearchName("")} className="hover:text-emerald-900">
                      <FaTimes size={10} />
                    </button>
                  </span>
                )}
                {searchCity && searchCity !== "all" && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                    City: {searchCity}
                    <button onClick={() => setSearchCity("")} className="hover:text-emerald-900">
                      <FaTimes size={10} />
                    </button>
                  </span>
                )}
                {searchLocation && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                    Area: {searchLocation}
                    <button onClick={() => setSearchLocation("")} className="hover:text-emerald-900">
                      <FaTimes size={10} />
                    </button>
                  </span>
                )}
              </div>
              <button
                onClick={clearFilters}
                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
              >
                <FaTimes size={12} />
                Clear All
              </button>
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="mb-6 flex justify-between items-center">
          <Text className="text-gray-600 text-sm">
            Showing <span className="font-semibold text-emerald-600">{filteredSellers.length}</span> of{" "}
            <span className="font-semibold">{sellers.length}</span> agents
          </Text>
        </div>

        {filteredSellers.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaStore className="text-gray-400 text-3xl" />
            </div>
            <Heading size="md" className="text-gray-600 mb-2">
              No agents found
            </Heading>
            <Text className="text-gray-500 mb-4">
              Try adjusting your search filters to find more agents
            </Text>
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
            {filteredSellers.map((seller) => (
              <Box
                key={seller.id}
                className={`bg-${cardBg} rounded-xl overflow-hidden border border-gray-200 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-xl`}
                style={{ boxShadow: shadowColor }}
              >
                {/* Image with hover effect */}
                <div className="relative group overflow-hidden h-48 bg-gradient-to-br from-emerald-100 to-teal-100">
                  {seller.profileImage || seller.thumbnail ? (
                    <img
                      src={seller.profileImage || seller.thumbnail || "/placeholder-showroom.jpg"}
                      alt={seller.sellerName}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-500">
                      <FaUserCircle className="text-white text-6xl" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                  {/* Hover overlay */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-black/50 backdrop-blur-sm">
                    <span className="bg-emerald-600 text-white text-xs px-3 py-1.5 rounded-full font-semibold shadow-lg transform group-hover:scale-105 transition-transform duration-300">
                      View Profile
                    </span>
                  </div>

                  {/* Badges */}
                  <div className="absolute top-3 right-3 flex space-x-2">
                    <Badge bg="emerald.600" color="white" px={2} py={1} borderRadius="full" fontSize="xs" className="shadow-md">
                      Featured
                    </Badge>
                    {seller?.timestamp && (
                      <Badge bg="gray.600" color="white" px={2} py={1} borderRadius="full" fontSize="xs">
                        {moment(seller?.timestamp?.toDate()).fromNow()}
                      </Badge>
                    )}
                  </div>

                  {/* Agent badge */}
                  <div className="absolute bottom-3 left-3">
                    <Badge bg="emerald.600" color="white" px={2} py={1} borderRadius="full" fontSize="xs">
                      Verified Agent
                    </Badge>
                  </div>
                </div>

                <div className="p-5">
                  <div className="flex items-start mb-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-emerald-500 to-teal-500 flex-shrink-0 mr-3">
                      {seller.profileImage || seller.thumbnail ? (
                        <img
                          src={seller.profileImage || seller.thumbnail}
                          alt={seller.sellerName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <FaUserCircle className="text-white text-xl" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <Heading as="h3" size="sm" className="text-gray-800 mb-1 line-clamp-1">
                        {seller.sellerName}
                      </Heading>
                      <Text className="text-gray-500 text-xs flex items-center">
                        <ImLocation className="inline mr-1 text-emerald-500" size={12} />
                        <span className="line-clamp-1">{seller.address || "Address not specified"}</span>
                      </Text>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <Box className="bg-emerald-50 p-2 rounded-lg text-center">
                      <Text fontSize="xs" className="text-gray-500">Listings</Text>
                      <Text className="font-bold text-emerald-600 text-sm">
                        {seller.listingsCount || "0"}
                      </Text>
                    </Box>
                    <Box className="bg-emerald-50 p-2 rounded-lg text-center">
                      <Text fontSize="xs" className="text-gray-500">Experience</Text>
                      <Text className="font-bold text-emerald-600 text-sm">
                        {seller.experience || "N/A"}
                      </Text>
                    </Box>
                  </div>

                  <Link to={`/seller-profile/${seller.id}`}>
                    <button className="w-full bg-emerald-600 text-white py-2 rounded-lg font-semibold hover:bg-emerald-700 transition-colors duration-300">
                      View Profile
                    </button>
                  </Link>
                </div>
              </Box>
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        .line-clamp-1 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 1;
        }
      `}</style>
    </div>
  );
};

export default SeeAllFeaturedSellers;